import React, { useState, useMemo, useEffect } from "react";
import { FileText, TrendingUp, Calendar, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, ResponsiveContainer } from "recharts";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "../../components/ui/chart";
import { Button } from "../../components/ui/button";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";
import { enUS } from "date-fns/locale";
import axios from "axios";
import { toast, Toaster } from "sonner"; 

const Revenue = () => {
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [dateRange, setDateRange] = useState([
    {
      startDate: firstDayOfMonth,
      endDate: today,
      key: "selection",
    },
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [allData, setAllData] = useState([]);

  // Function to group bookings by date and sum amounts (excluding rejected)
  const groupBookingsByDate = (bookings) => {
    const dateAmounts = {};
    
    bookings.forEach((booking) => {
      if (booking.status === "rejected") return;
      
      const date = booking.createdAt.split("T")[0];
      dateAmounts[date] = (dateAmounts[date] || 0) + (booking.amount || 0);
    });

    const result = Object.entries(dateAmounts).map(([date, amount]) => ({
      date,
      amount: parseFloat(amount.toFixed(2)), // Round to 2 decimal places
    }));

    result.sort((a, b) => new Date(a.date) - new Date(b.date));
    return result;
  };

  // Fetch bookings data
  useEffect(() => {
    const fetchBookings = async () => {
      const token = localStorage.getItem("adminToken");
      if (!token) return;

      try {
        setIsLoading(true);
        const res = await axios.get(
          "https://wavetechservices.in/admin/bookings",
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setBookings(res.data.data);
        
        // Process data after fetching
        const revenueData = groupBookingsByDate(res.data.data);
        setAllData(revenueData);
//          if (revenueData.length > 0) {
//   const firstDate = new Date(revenueData[0].date);
//   const lastDate = new Date(revenueData[revenueData.length - 1].date);

//   setDateRange([
//     {
//       startDate: firstDate < firstDayOfMonth ? firstDate : firstDayOfMonth,
//       endDate: lastDate > today ? today : lastDate,
//       key: "selection",
//     },
//   ]);
// }

      } catch (err) {
        console.error("Error fetching bookings:", err);
        toast.error("Failed to fetch bookings");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBookings();
  }, []);

  // Filter data based on selected date range
const filteredData = useMemo(() => {
  if (!allData.length) return [];

  const start = new Date(dateRange[0].startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(dateRange[0].endDate);
  end.setHours(23, 59, 59, 999);

  return allData
    .filter((item) => {
      const itemDate = new Date(item.date);
      return itemDate >= start && itemDate <= end;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date)); // Also sort here to ensure sorted
}, [dateRange, allData]);


  const totalRevenue = useMemo(
    () => filteredData.reduce((sum, item) => sum + item.amount, 0),
    [filteredData]
  );

  // Calculate percentage change compared to previous period
  const percentageChange = useMemo(() => {
    if (filteredData.length < 2) return 0;
    
    const currentPeriod = filteredData.slice(-1)[0]?.amount || 0;
    const previousPeriod = filteredData.slice(-2)[0]?.amount || 0;
    
    if (previousPeriod === 0) return 0;
    return ((currentPeriod - previousPeriod) / previousPeriod) * 100;
  }, [filteredData]);

  // Chart configuration
  const chartConfig = {
    amount: {
      label: "Revenue",
      color: "#0a67f2",
    },
  };

  // Download functions
  const downloadCSV = () => {
    const csvData = filteredData.map(item => ({
      Date: item.date,
      Revenue: item.amount
    }));
    
    const csvHeaders = "Date,Revenue\n";
    const csvContent = csvHeaders + csvData.map(item => 
      `${item.Date},${item.Revenue}`
    ).join("\n");
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `revenue_${format(dateRange[0].startDate, 'yyyyMMdd')}_${format(dateRange[0].endDate, 'yyyyMMdd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card className='lg:w-[90%]'>
      <Toaster />
      <CardHeader>
        <div className='flex justify-between items-start flex-col md:flex-row gap-4'>
          <div>
            <CardTitle>Total Revenue</CardTitle>
            <CardDescription>
              {format(dateRange[0].startDate, "MMM dd, yyyy")} - {format(dateRange[0].endDate, "MMM dd, yyyy")}
            </CardDescription>
          </div>
            {/* <div className="flex gap-3">
            <Button 
                variant="outline" 
                onClick={() => setShowCalendar(!showCalendar)}
                className="text-muted-foreground"
            >
                <Calendar className="mr-2 h-4 w-4" />
                Select Date Range
            </Button>
            <Button 
                variant="outline" 
                onClick={downloadCSV}
                disabled={isLoading || filteredData.length === 0}
            >
              {isLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Download Data
            </Button> 
          </div> */}
          <div className="flex gap-3 relative">
  <div className="relative">
    <Button 
      variant="outline" 
      onClick={() => setShowCalendar(!showCalendar)}
      className="text-muted-foreground"
    >
      <Calendar className="mr-2 h-4 w-4" />
      Select Date Range
    </Button>
    {showCalendar && (
      <div className="absolute right-0 mt-2 z-50 shadow-lg bg-white border rounded-lg">
        <DateRange
          editableDateInputs={true}
          onChange={(item) => {
            setDateRange([item.selection]);
            setShowCalendar(false);
          }}
          moveRangeOnFirstSelection={false}
          ranges={dateRange}
          maxDate={new Date()}
          locale={enUS}
        />
      </div>
    )}
  </div>
  
  <Button 
    variant="outline" 
    onClick={downloadCSV}
    disabled={isLoading || filteredData.length === 0}
  >
    {isLoading ? (
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
    ) : (
      <FileText className="mr-2 h-4 w-4" />
    )}
    Download Data
  </Button> 
</div>

        </div>

        {showCalendar && (
          <div className="absolute right-0 top-full z-50 mt-2 shadow-lg">
            <DateRange
              editableDateInputs={true}
              onChange={(item) => {
                setDateRange([item.selection]);
                setShowCalendar(false);
              }}
              moveRangeOnFirstSelection={false}
              ranges={dateRange}
              maxDate={new Date()}
              locale={enUS}
            />
          </div>
        )}
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center h-[250px]">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : filteredData.length === 0 ? (
          <div className="flex items-center justify-center h-[250px]">
            <p className="text-muted-foreground">No revenue data available for the selected date range</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig}>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={filteredData}
                margin={{
                  left: 12,
                  right: 12,
                  top: 12,
                  bottom: 12,
                }}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  minTickGap={32}
                  tickFormatter={(value) => {
                    return format(new Date(value), "MMM dd");
                  }}
                />
                <YAxis 
                  tickLine={false}
                  axisLine={false}
                  tickMargin={8}
                  tickFormatter={(value) => `₹ ${value}`}
                />

                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      className="w-[150px]"
                      labelFormatter={(value) => {
                        return format(new Date(value), "MMM dd, yyyy");
                      }}
                      formatter={(value) => [`₹ ${value.toFixed(2)}`, '']}
                    />
                  }
                />
                <Bar
                  dataKey="amount"
                  fill={chartConfig.amount.color}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>

      <CardFooter className="flex-col items-start gap-2 text-sm">
        <div className="flex gap-2 font-medium leading-none">
          {percentageChange >= 0 ? (
            <>
              Trending up by {Math.abs(percentageChange).toFixed(1)}% 
              <TrendingUp className="h-4 w-4 text-green-500" />
            </>
          ) : (
            <>
              Trending down by {Math.abs(percentageChange).toFixed(1)}% 
              <TrendingUp className="h-4 w-4 text-red-500 transform rotate-180" />
            </>
          )}
        </div>
        <div className="leading-none text-muted-foreground">
          Total revenue: ₹{totalRevenue.toFixed(2)} (excluding rejected bookings)
        </div>
      </CardFooter>
    </Card>
  );
};

export default Revenue;